import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { loadCookiesData, INTENTS } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import {
  apiClient,
  AddPropertyPageLoaderResult,
} from "~/services/apiClient";

export const AddPropertyPageLoader = async ({
  request,
}: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) redirect("/explore");
  const userUID = user!.uid;
  const authAccessInfo = await loadCookiesData(userUID);
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<AddPropertyPageLoaderResult>(
    "add-property-page",
    {
      ownerID,
      ownerType,
      userID: userUID,
      isAdminOrSuperAdmin: String(isAdminOrSuperAdmin),
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load add property page:", result.error);
    throw new Error(result.error ?? "Failed to load add property page");
  }

  return {
    canCreateProperty: result.data.canCreateProperty,
    isAuthenticated: true,
    subscriptionPlan: result.data.subscriptionPlan,
    currentPropertyCount: result.data.currentPropertyCount,
    maxProperties: result.data.maxProperties,
  };
};

export type AddPropertyPageLoaderType = {
  canCreateProperty: boolean;
  isAuthenticated: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  currentPropertyCount: number;
  maxProperties: number;
};

export const AddPropertyPageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType } = authAccessInfo.data;

  const payload = JSON.parse(
    formData.get("body") as string
  ) as Record<string, unknown>;

  return withEntityCacheInvalidation([{ entity: "property" }], async () => {
    const result = await apiClient.sendIntent<{ id: string }>(
      INTENTS._add_property,
      { ...payload, ownerID, ownerType },
    );

    if (result.success && result.data?.id) {
      return redirect(`/property/${result.data.id}`);
    }
    return result;
  });
};
